/**
 * Global search page — /app/search + /admin/search.
 *
 * Mobile pattern: TopBar search icon navigates here instead of expanding
 * inline. Desktop dropdown handles quick lookups; this page is the fuller
 * view with actual thumbnail rows.
 *
 * Rows render like Frame.io's search: thumbnail (video/image preview or
 * asset icon), duration chip on video/audio, bold title, meta line with
 * asset type + project name. Item count at the bottom.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, FileImage, FolderKanban, Music2, Search, Video as VideoIcon } from "lucide-react";
import { useSearch } from "@/hooks/use-search";
import { useProjects } from "@/hooks/use-projects";
import { useWindowedList } from "@/hooks/use-windowed-list";
import { formatDurationChip } from "@/components/projects/video-card";
import type { Project, Video } from "@/lib/api-types";

export default function SearchPage() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAdmin = pathname.startsWith("/admin/");
  const basePath = isAdmin ? "/admin" : "/app";
  const projectsBasePath = `${basePath}/projects`;
  const watchBasePath = `${basePath}/watch`;

  // Initial query from ?q= so shared/back-nav URLs work.
  const [query, setQuery] = useState(searchParams.get("q") ?? "");
  const inputRef = useRef<HTMLInputElement>(null);
  const { projects, videos, isLoading } = useSearch(query);
  const { data: allProjects } = useProjects();

  const projectNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of allProjects ?? []) map.set(p.id, p.name);
    return map;
  }, [allProjects]);

  // Videos are the unbounded side of a search (title matches across the whole
  // workspace); window them so a broad query doesn't mount thousands of rows.
  const { visible: pagedVideos, hasMore, sentinelRef } = useWindowedList(videos);

  const hasQuery = query.trim().length > 0;
  const totalCount = projects.length + videos.length;
  const noResults = hasQuery && !isLoading && totalCount === 0;

  // Auto-focus the input on mount.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Reflect query in URL (debounced-ish — write on every change is fine here,
  // React Router just replaces the string in the address bar).
  useEffect(() => {
    if (query) setSearchParams({ q: query }, { replace: true });
    else if (searchParams.get("q")) setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  return (
    <div className="mx-auto flex h-[calc(100dvh-3.5rem)] w-full max-w-3xl flex-col px-3 py-3 md:h-[calc(100dvh-4rem)] md:px-8 md:py-6">
      {/* Search input row */}
      <div className="flex shrink-0 items-center gap-2">
        <button
          type="button"
          aria-label="Back"
          onClick={() => navigate(-1)}
          className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground md:hidden"
        >
          <ArrowLeft className="h-5 w-5" strokeWidth={1.75} />
        </button>
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search"
            className="h-11 w-full rounded-full border border-border bg-secondary/50 pl-11 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-ring focus:bg-background focus:outline-none"
          />
        </div>
      </div>

      {/* Results / empty state */}
      <div className="mt-4 min-h-0 flex-1 overflow-y-auto">
        {!hasQuery && <EmptyHint />}
        {hasQuery && isLoading && totalCount === 0 && <LoadingState />}
        {noResults && <NoResults query={query} />}

        {hasQuery && totalCount > 0 && (
          <div className="flex flex-col gap-2">
            {projects.map((p) => (
              <ProjectRow key={`p:${p.id}`} project={p} to={`${projectsBasePath}/${p.id}`} />
            ))}
            {pagedVideos.map((v) => (
              <VideoRow
                key={`v:${v.id}`}
                video={v}
                projectName={projectNameById.get(v.projectId)}
                to={`${watchBasePath}/${v.id}`}
              />
            ))}
            {hasMore && <div ref={sentinelRef} aria-hidden className="h-1" />}
          </div>
        )}
      </div>

      {/* Item count footer — matches Frame.io */}
      {hasQuery && totalCount > 0 && (
        <div className="shrink-0 border-t border-border/60 pt-3 text-xs text-muted-foreground">
          {totalCount} {totalCount === 1 ? "item" : "items"}
        </div>
      )}
    </div>
  );
}

// ── Rows ────────────────────────────────────────────────────────────────────

function ProjectRow({ project, to }: { project: Project; to: string }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2.5 transition-colors hover:bg-secondary/50"
    >
      <div className="grid h-14 w-20 shrink-0 place-items-center overflow-hidden rounded-md bg-secondary/60">
        <FolderKanban className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-foreground">{project.name}</div>
        <div className="truncate text-xs text-muted-foreground">Project</div>
      </div>
    </Link>
  );
}

function VideoRow({ video, projectName, to }: { video: Video; projectName?: string; to: string }) {
  const duration = formatDurationChip(video.durationSeconds, video.assetType);
  const typeLabel =
    video.assetType === "audio" ? "Audio" : video.assetType === "image" ? "Image" : "Video";
  return (
    <Link
      to={to}
      className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2.5 transition-colors hover:bg-secondary/50"
    >
      <div className="relative grid h-14 w-20 shrink-0 place-items-center overflow-hidden rounded-md bg-secondary/60">
        {video.assetType === "video" && video.cdnUrl ? (
          <video src={video.cdnUrl} muted preload="metadata" className="h-full w-full object-cover" />
        ) : video.assetType === "image" && video.cdnUrl ? (
          <img src={video.cdnUrl} alt={video.title} loading="lazy" className="h-full w-full object-cover" />
        ) : video.assetType === "audio" ? (
          <Music2 className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
        ) : video.assetType === "image" ? (
          <FileImage className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
        ) : (
          <VideoIcon className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
        )}
        {duration && (
          <span className="pointer-events-none absolute bottom-0.5 right-0.5 rounded bg-black/70 px-1 text-[10px] font-medium tabular-nums text-white">
            {duration}
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-foreground">{video.title}</div>
        <div className="truncate text-xs text-muted-foreground">
          {typeLabel}
          {projectName && (
            <>
              <span className="mx-1.5">·</span>
              {projectName}
            </>
          )}
        </div>
      </div>
    </Link>
  );
}

// ── States ──────────────────────────────────────────────────────────────────

function EmptyHint() {
  return (
    <div className="flex flex-col items-center gap-3 py-16 text-center">
      <div className="grid h-14 w-14 place-items-center rounded-2xl bg-secondary">
        <Search className="h-6 w-6 text-muted-foreground" strokeWidth={1.5} />
      </div>
      <div className="text-sm font-medium text-foreground">
        Search for projects and files
      </div>
      <p className="max-w-xs text-xs text-muted-foreground">
        Start typing to look up any project or file in your workspace.
      </p>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2.5">
          <div className="h-14 w-20 shrink-0 animate-pulse rounded-md bg-secondary/60" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-3 w-3/4 animate-pulse rounded bg-secondary/60" />
            <div className="h-2.5 w-1/2 animate-pulse rounded bg-secondary/40" />
          </div>
        </div>
      ))}
    </div>
  );
}

function NoResults({ query }: { query: string }) {
  return (
    <div className="flex flex-col items-center gap-2 py-16 text-center">
      <div className="text-sm font-medium text-foreground">No matches for "{query}"</div>
      <p className="text-xs text-muted-foreground">Try a different word or check for typos.</p>
    </div>
  );
}
